import type { BatchAssignment, SampleGroup, TrayPath } from '@ctcells/contract'

/**
 * 새 작업의 **시료 ↔ 웰 배정** 규칙 — `POST /batches` 의 `assignments`(신설 2026-09-22).
 *
 * 🔴 서버 규칙 넷(`ctcells-core app/services/batches.py` `assignment_problem`) 중
 *    화면이 미리 말해 줄 수 있는 것은 ⓒ 중복 · ⓓ 전건 둘이다. ⓐ 웰이 `tray_path.order` 안은
 *    고르개가 그 목록만 내놓아 지키고, ⓑ `sample_id` 가 `sample_ids` 안은 고른 시료로만 지어 지킨다.
 * 🔴 **전건 아니면 안 보낸다.** 반쯤 배정된 배치는 서버가 422(`partial_assignment`)로 떨군다 —
 *    「같은 배치 안에서 귀속 해석 규칙이 갈아탄다」가 서버 근거다. 화면은 그 전에 막는다.
 * 🔴 **추론으로 자동 배정하지 않는다.** 묶음(`sample_ref`)은 사람이 맞추도록 **보여 주기만** 한다.
 *    ⊕ 2026-10-21 ~ 10-26 에는 «담은 순서대로» 칸을 정했다(`orderedSlots`). **2026-10-27 걷었다** — 이제 사람이 웰마다 고른다
 *      (`pickWell` · eric 「해당 칸 또는 웰 별로 하는 건 어때?」 · 「응 한번 해봐」). 담은 차례 자동 배정을 되살리지 마라.
 */

/** 시료 id → 고른 웰. 안 고른 시료는 키가 없다 */
export type WellPicks = Readonly<Record<string, string>>

export type AssignmentsDecision =
  /** 하나도 안 골랐다 — `assignments` 키를 **싣지 않는다**(배정 없이 뜬다) */
  | { kind: 'none' }
  /** 일부만 골랐다 — 보내지 않는다. `missing` 은 웰이 없는 시료 */
  | { kind: 'partial'; missing: readonly string[] }
  /** 같은 웰을 둘 이상이 잡았다 — 보내지 않는다(고르개가 막지만 한 번 더 지킨다) */
  | { kind: 'duplicate'; wells: readonly string[] }
  /** 전건 — 이것만 싣는다. 순서는 고른 시료 순서다 */
  | { kind: 'full'; assignments: readonly BatchAssignment[] }

/**
 * 고른 시료와 웰 선택으로 본문의 `assignments` 를 가른다.
 * ⚠ `picks` 에 남아 있어도 **고른 시료가 아니면 무시한다** — 시료를 뺐는데 배정이 남아 나가면
 *   서버가 ⓑ(`sample_outside_sample_ids`)로 떨군다.
 */
export function assignmentsBody(
  selected: readonly string[],
  picks: WellPicks,
): AssignmentsDecision {
  const pairs = selected.flatMap((sampleId) => {
    const well = picks[sampleId]
    // 칸 이름은 정본 v0.9.3 `wellName`(오픈트론 이름 · 옛 `well`)
    return well === undefined || well === '' ? [] : [{ sample_id: sampleId, wellName: well }]
  })
  if (pairs.length === 0) return { kind: 'none' }

  const seen = new Set<string>()
  const dup = new Set<string>()
  for (const { wellName } of pairs) {
    if (seen.has(wellName)) dup.add(wellName)
    seen.add(wellName)
  }
  if (dup.size > 0) return { kind: 'duplicate', wells: [...dup] }

  if (pairs.length < selected.length) {
    const has = new Set(pairs.map((p) => p.sample_id))
    return { kind: 'partial', missing: selected.filter((id) => !has.has(id)) }
  }
  return { kind: 'full', assignments: pairs }
}

/** 그 웰을 잡은 **다른** 시료. 없으면 `undefined` */
export function wellHolder(
  picks: WellPicks,
  well: string,
  exceptSampleId: string,
): string | undefined {
  for (const [sampleId, picked] of Object.entries(picks)) {
    if (picked === well && sampleId !== exceptSampleId) return sampleId
  }
  return undefined
}

/**
 * 그 웰을 담은 레시피 묶음. 🔴 **표시용이다** — 서버가 이 묶음으로 `sample_ref` 를 파생한다.
 * ⚠ `sample_ref` 는 스키마가 선택으로 둔다 — 없으면 대체값(웰 이름 · `role`)을 짓지 않는다.
 */
export function groupOfWell(trayPath: TrayPath | undefined, well: string): SampleGroup | undefined {
  return trayPath?.sample_groups?.find((group) => group.wells.includes(well))
}

/** 레시피가 준 웰 순서. 없거나 비면 배정할 수 없다(서버 `recipe_has_no_tray_path`) */
export function trayWells(trayPath: TrayPath | undefined): readonly string[] {
  return trayPath?.order ?? []
}

/** 레시피의 시료 칸 묶음 — 서버 요약(`POST /recipes/summary`)의 `samples` 한 줄(P1 · REF ↔ 웰들) */
export interface SlotGroup {
  sample_ref: string
  wells: readonly string[]
}

/**
 * **웰마다 사람이 고른다**(eric 2026-10-27 「해당 칸 또는 웰 별로 하는 건 어때?」 → 웰 · 「응 한번 해봐」).
 * 트레이 그림의 웰을 누르면 그 웰 곁의 시료 선택 창에서 하나를 고른다. 🔴 **자동 배정 · 순서 추론이 없다** —
 * 화면이 칸을 정하지 않고, 사람이 웰마다 고른 것만 `WellPicks` 에 든다. 본문은 종전 그대로 `assignmentsBody` 가 짓는다.
 */

/** 레시피의 시료 자리 전부(묶음 차례 그대로 · 웰 하나씩) — 표준 레시피는 P1 A1 · P1 B1 · P2 A2 … REF C3 */
export function slotWells(
  groups: readonly SlotGroup[],
): readonly { sampleRef: string; wellName: string }[] {
  return groups.flatMap((group) =>
    group.wells.map((wellName) => ({ sampleRef: group.sample_ref, wellName })),
  )
}

/**
 * 그 웰에 그 시료를 둔다. 웰에 든 다른 시료는 빠진다(웰 하나 = 시료 하나).
 * 🔴 **다른 웰에 이미 든 시료는 옮기지 않는다** — 실수 방지로 고르개가 비활성 + 사유로 막는다(팀장 결정 2026-10-27).
 *    여기서도 한 번 더 지켜 그대로 돌려준다.
 */
export function pickWell(picks: WellPicks, sampleId: string, well: string): WellPicks {
  const at = picks[sampleId]
  if (at !== undefined && at !== well) return picks
  const holder = wellHolder(picks, well, sampleId)
  const next: Record<string, string> = { ...picks, [sampleId]: well }
  if (holder !== undefined) delete next[holder]
  return next
}

/** 그 웰을 비운다. 든 것이 없으면 그대로 */
export function clearWell(picks: WellPicks, well: string): WellPicks {
  const entries = Object.entries(picks).filter(([, picked]) => picked !== well)
  return entries.length === Object.keys(picks).length ? picks : Object.fromEntries(entries)
}

/**
 * 고른 시료(본문의 `sample_ids`) — 레시피 웰 차례대로. 🔴 레시피 밖 웰의 배정은 버린다(레시피를 바꾸면 그 웰이 없다 ·
 * 서버 ⓐ `well_outside_tray_path` 를 만들지 않는다).
 */
export function pickedInWellOrder(picks: WellPicks, groups: readonly SlotGroup[]): string[] {
  return slotWells(groups).flatMap(({ wellName }) => {
    const id = wellHolder(picks, wellName, '')
    return id === undefined ? [] : [id]
  })
}

/*
 * ── 한 묶음 = 한 환자(eric 2026-10-29 「P1 두 개의 환자 번호가 같아야 하잖아. 쉽게 표현하는 방법이 없을까?」) ──
 * 🔴 근거: 레시피 저장 검증(core `recipe_v2/validate.py` `cross_contamination_issues`)이 묶음(`sample_ref`)을 «환자 하나»로 보고
 *    팁을 묶음 안에서만 같이 쓴다. 한 묶음에 다른 환자가 들면 한 팁이 두 환자를 빤다. 🔴 배치 생성 서버(`assignment_problem`)는
 *    이 대조를 하지 않는다(2026-10-29 확인) — 화면이 고르기를 좁히고, 어긋나면 [추가]를 막는다.
 * ⚠ 웰이 둘 이상인 묶음만 대상이다(REF 처럼 한 칸짜리는 대조할 짝이 없다). 묶음 역할(`role`)은 요약에 없어 짓지 않는다.
 * ⚠ 환자 번호가 빈 시료는 «빈 번호» 하나의 환자로 친다 — 빈 것과 있는 것을 같은 환자로 짐작하지 않는다
 *   (서버 사전 점검도 빈 번호를 `patient_id_missing` 으로 막는다).
 */

/** 환자 대조 열쇠 — 앞뒤 공백을 걷은 값 · 비었으면 `''` */
export function patientKey(patientId: string | null | undefined): string {
  return patientId?.trim() ?? ''
}

export interface GroupPatient {
  sampleRef: string
  wells: readonly string[]
  /** 묶음 안 웰에 든 시료들의 환자 열쇠(웰 차례 · 시료를 못 찾은 웰은 빠진다) */
  keys: readonly string[]
  /** 묶음의 환자 — 처음 고른 시료의 원래 값(가리기 전). 아무것도 안 골랐거나 번호가 비면 `undefined` */
  patientId: string | undefined
  /** 한 묶음에 서로 다른 환자가 들었다 */
  mismatch: boolean
}

/** 묶음마다 든 시료의 환자 — 웰이 둘 이상인 묶음만 */
export function groupPatients(
  groups: readonly SlotGroup[],
  picks: WellPicks,
  patientOf: (sampleId: string) => string | null | undefined | false,
): readonly GroupPatient[] {
  return groups
    .filter((group) => group.wells.length >= 2)
    .map((group) => {
      const held = group.wells.flatMap((well) => {
        const id = wellHolder(picks, well, '')
        if (id === undefined) return []
        const patient = patientOf(id)
        return patient === false ? [] : [patient]
      })
      const keys = held.map(patientKey)
      const first = held.find((p) => patientKey(p) !== '')
      return {
        sampleRef: group.sample_ref,
        wells: group.wells,
        keys,
        patientId: keys[0] === '' || first == null ? undefined : first.trim(),
        mismatch: new Set(keys).size > 1,
      }
    })
}

/**
 * 그 웰의 시료 고르기를 좁힐 환자 — 같은 묶음의 «다른» 웰에 든 시료의 환자 열쇠. 좁힐 것이 없으면 `undefined`
 * (한 칸짜리 묶음 · 다른 웰이 비었다). 첫 시료를 빼면 제한도 풀린다.
 */
export function patientLockFor(
  groups: readonly SlotGroup[],
  picks: WellPicks,
  well: string,
  patientOf: (sampleId: string) => string | null | undefined | false,
): { key: string; patientId: string | undefined; sampleRef: string } | undefined {
  const group = groups.find((g) => g.wells.includes(well))
  if (group === undefined || group.wells.length < 2) return undefined
  for (const other of group.wells) {
    if (other === well) continue
    const id = wellHolder(picks, other, '')
    if (id === undefined) continue
    const patient = patientOf(id)
    if (patient === false) continue
    const key = patientKey(patient)
    return { key, patientId: key === '' ? undefined : key, sampleRef: group.sample_ref }
  }
  return undefined
}
